import React from 'react';

import {
    CheckCircle,
    AlertTriangle,
    Edit,
} from 'lucide-react';
import { marked } from 'marked';
import { useTheme } from '../../utils/theme';   


const SlamEvaluationResult = ({ evaluation, isLoading, onSave, saveStatus }) => {
    const { theme } = useTheme();
    const isDarkMode = true; // OLED: always dark

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-8">
                <div className="w-10 h-10 border-t-4 border-b-4 border-green-500 rounded-full animate-spin mr-3"></div>
                <p className={`text-lg font-medium ${'text-gray-400'}`}>Running SLAM evaluation...</p>
            </div>
        );
    }

    if (!evaluation) {
        return (
            <div className={`py-8 text-center ${'text-gray-400'}`}>
                No evaluation data available.
            </div>
        );
    }

    const getScoreColor = (score) => {
        const numScore = parseInt(score.split('/')[0]);
        const maxScore = parseInt(score.split('/')[0]);
        if (numScore >= 75) return `${'text-green-400'}`;
        if (numScore >= 60) return `${'text-green-300'}`;
        if (numScore >= 45) return `${'text-yellow-400'}`;
        return `${'text-red-400'}`;
    };

    const renderMarkdown = (content) => {
        if (!content) return null;

        return (
            <div
                dangerouslySetInnerHTML={{
                    __html: marked(content, { breaks: true })
                }}
                className={`prose prose-sm max-w-none ${'text-gray-300'}`}
            />
        );
    };

    // Format the evaluation data for display
    return (
        <div className={`space-y-6 p-2 ${'bg-gray-800'}`}>
            {/* Save Evaluation Button */}
            <div className="flex justify-end">
                <button
                    onClick={() => onSave && onSave(evaluation)}
                    disabled={isLoading || saveStatus === 'saving'}
                    className={`px-4 py-2 rounded-md ${'text-gray-900'} flex items-center ${saveStatus === 'saved'
                        ? 'bg-green-500 hover:bg-green-600'
                        : saveStatus === 'error'
                            ? 'bg-red-500 hover:bg-red-600'
                            : saveStatus === 'saving'
                                ? 'bg-blue-400 cursor-not-allowed'
                                : 'bg-blue-500 hover:bg-blue-600'
                        }`}
                >
                    {saveStatus === 'saving' ? (
                        <>
                            <div className="w-4 h-4 border-t-2 border-b-2 ${'border-gray-400'} rounded-full animate-spin mr-2"></div>
                            Saving...
                        </>
                    ) : saveStatus === 'saved' ? (
                        <>
                            <CheckCircle size={16} className={`mr-2 ${'text-green-400'}`} />
                            Saved
                        </>
                    ) : saveStatus === 'error' ? (
                        <>
                            <AlertTriangle size={16} className={`mr-2 ${'text-red-400'}`} />
                            Error Saving
                        </>
                    ) : (
                        <>
                            <Edit size={16} className={`mr-2 ${'text-blue-400'}`} />
                            Save Evaluation
                        </>
                    )}
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Statistical Analysis */}
                <div className={`${'bg-gray-700'} rounded-lg shadow p-4`}>
                    <h3 className="text-lg font-semibold mb-3">Statistical Analysis</h3>
                    <div className={`prose prose-sm max-w-none ${'text-gray-300'}`}>
                        {evaluation.statistical_analysis && renderMarkdown(evaluation.statistical_analysis)}
                    </div>
                </div>

                {/* Character Evaluation */}
                    <div className={`${'bg-gray-700'} rounded-lg shadow p-4`}>
                    <h3 className="text-lg font-semibold mb-3">Character Assessment</h3>
                    <div className={`prose prose-sm max-w-none ${'text-gray-300'}`}>
                        {evaluation.character_evaluation && renderMarkdown(evaluation.character_evaluation)}
                    </div>
                </div>
            </div>

            {/* Display events if available */}
            {evaluation.events && evaluation.events.length > 0 && evaluation.events[0].content && (
                <div className={`${'bg-gray-700'} rounded-lg shadow p-4`}>
                    <h3 className="text-lg font-semibold mb-3">Comprehensive Evaluation</h3>

                    {evaluation.events[0].content.includes("final_evaluation") && (
                        <div className={`space-y-4 ${'text-gray-300'}`}>
                            {/* Extract and display the structured evaluation */}
                            {(() => {
                                try {
                                    // Extract the JSON block from the content
                                    const jsonMatch = evaluation.events[0].content.match(/```json\n([\s\S]*?)\n```/);
                                    if (jsonMatch && jsonMatch[1]) {
                                        const evalData = JSON.parse(jsonMatch[1]);

                                        return (
                                            <div className="space-y-6">
                                                {/* Offensive/Defensive Skills */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {evalData.skill_assessment.offensive && (
                                                        <div className={`${'bg-gray-600'} p-3 rounded-lg`}>
                                                            <h4 className="font-medium mb-2">Offensive Skills</h4>
                                                            <div className="space-y-2">
                                                                {Object.entries(evalData.skill_assessment.offensive).map(([key, value]) => (
                                                                    <div key={key} className="flex justify-between">
                                                                        <span className={`${'text-gray-300'} capitalize`}>{key.replace(/_/g, ' ')}</span>
                                                                        <span className={getScoreColor(value)}>{value}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {evalData.skill_assessment.defensive && (
                                                        <div className={`${'bg-gray-600'} p-3 rounded-lg`}>
                                                            <h4 className="font-medium mb-2">Defensive Skills</h4>
                                                            <div className="space-y-2">
                                                                {Object.entries(evalData.skill_assessment.defensive).map(([key, value]) => (
                                                                    <div key={key} className="flex justify-between">
                                                                        <span className={`${'text-gray-300'} capitalize`}>{key.replace(/_/g, ' ')}</span>
                                                                        <span className={getScoreColor(value)}>{value}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Portal Analysis */}
                                                {evalData.portal_analysis && (
                                                    <div className={`${'bg-gray-600'} p-3 rounded-lg`}>
                                                        <h4 className="font-medium mb-2">Portal Analysis</h4>

                                                        {evalData.portal_analysis.transfer_history && (
                                                            <div className="mb-3">
                                                                <span className={`font-medium ${'text-gray-300'}`}>Transfer History: </span>
                                                                <span>{evalData.portal_analysis.transfer_history}</span>
                                                            </div>
                                                        )}

                                                        {evalData.portal_analysis.red_flags && evalData.portal_analysis.red_flags.length > 0 && (
                                                            <div className="mb-3">
                                                                <span className={`font-medium ${'text-gray-300'}`}>Red Flags:</span>
                                                                <ul className="list-disc pl-5 mt-1 text-red-600">
                                                                    {evalData.portal_analysis.red_flags.map((flag, i) => (
                                                                        <li key={i}>{flag}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}

                                                        {evalData.portal_analysis.hidden_gems && evalData.portal_analysis.hidden_gems.length > 0 && (
                                                            <div className="mb-3">
                                                                <span className={`font-medium ${'text-gray-300'}`}>Hidden Gems:</span>
                                                                <ul className="list-disc pl-5 mt-1 text-green-600">
                                                                    {evalData.portal_analysis.hidden_gems.map((gem, i) => (
                                                                        <li key={i}>{gem}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}

                                                        {evalData.portal_analysis.timeline_urgency && (
                                                            <div>
                                                                <span className={`font-medium ${'text-gray-300'}`}>Timeline Urgency: </span>
                                                                <span className={`${'text-yellow-400'}`}>{evalData.portal_analysis.timeline_urgency}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                } catch (error) {
                                    console.error("Error parsing evaluation JSON:", error);
                                }

                                // Fallback to display raw content if JSON parsing fails
                                const cleanedContent = evaluation.events[0].content.replace(/```json[\s\S]*?```/g, '');
                                return renderMarkdown(cleanedContent);
                            })()}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SlamEvaluationResult;